"use client";

import { useState, useEffect } from "react";
import { Chip, Typography, Box } from "@mui/material";
import type { Migration } from "./types";
import type { Column } from "../../common/Table/types";
import { Pagination } from "../../common/Table/Pagination";
import { Table } from "../../common/Table/Table";
import { parse } from "date-fns";

// Modificar la interfaz MigrationTableProps para incluir onViewMigration
interface MigrationTableProps {
  migrations: Migration[];
  isLoading?: boolean;
  onViewMigration?: (migrationId: string) => void;
  emptyMessage?: string;
}

export default function MigrationTable({
  migrations,
  isLoading = false,
  onViewMigration,
  emptyMessage = "There are no migrations generated",
}: MigrationTableProps) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortColumn, setSortColumn] = useState<string>("startDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [sortedData, setSortedData] = useState<Migration[]>(migrations);

  // Actualizar los datos ordenados cuando cambian las migraciones o el ordenamiento
  useEffect(() => {
    const sorted = [...migrations].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case "id":
          comparison = a.id.localeCompare(b.id);
          break;
        case "migrationName":
          comparison = (a.migrationName || "").localeCompare(b.migrationName || "");
          break;
        case "startDate":
          try {
            const dateA = parse(a.startDate, "M/d/yyyy h:mm:ssa", new Date());
            const dateB = parse(b.startDate, "M/d/yyyy h:mm:ssa", new Date());
            comparison = dateA.getTime() - dateB.getTime();
          } catch (error) {
            console.error("Error parsing dates:", error);
            comparison = 0;
          }
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
    setSortedData(sorted);
  }, [migrations, sortColumn, sortDirection]);

  const getStatusChip = (status: string) => {
    let bgcolor = "";
    let displayStatus = status;

    // Normalizar el estado para manejar diferentes casos de "Generated" o "Error"
    if (status.toUpperCase() === "FAILED" || status.toUpperCase() === "ERROR") {
      displayStatus = "REVIEW REQUIRED";
      bgcolor = "#f5f5f5"; // Fondo gris claro para todos los casos
    } else {
      switch (status) {
        case "MIGRATED":
          bgcolor = "#f5f5f5";
          break;
        case "Done":
          bgcolor = "#e8f5e9";
          break;
        case "Example":
          bgcolor = "#e3f2fd";
          break;
        default:
          bgcolor = "#f5f5f5";
      }
    }

    return (
      <Chip
        label={displayStatus}
        size="small"
        sx={{
          bgcolor,
          color: "text.primary",
          borderRadius: 1,
          fontWeight: "medium",
          px: 1,
        }}
      />
    );
  };

  const handleSort = (columnId: string) => {
    // Forzar una nueva referencia para el estado
    if (sortColumn === columnId) {
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      console.log("Changing direction to:", newDirection);
      setSortDirection(newDirection);
    } else {
      console.log("Changing column from", sortColumn, "to", columnId);
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  // Calcular datos paginados
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Definir solo las columnas solicitadas
  const columns: Column<Migration>[] = [
    {
      id: "migrationName",
      label: "Migration Name",
      accessor: (item) => item.migrationName || "",
      sortable: true,
      width: "50%", // Ancho fijo
    },
    {
      id: "startDate",
      label: "Execution date",
      accessor: (item) => item.startDate,
      sortable: true,
      width: "25%", // Ancho fijo
    },
    {
      id: "status",
      label: "Status",
      accessor: (item) => getStatusChip(item.status),
      sortable: true,
      width: "15%", // Ancho fijo
    },
    {
      id: "actions",
      label: "",
      accessor: (item: Migration) => (
        <Typography
          variant="body2"
          color="primary"
          sx={{
            cursor: "pointer",
            fontWeight: "medium",
          }}
          onClick={(e) => {
            e.stopPropagation(); // Evitar que se propague al onRowClick de la fila
            if (onViewMigration) {
              onViewMigration(item.id);
            }
          }}
        >
          View
        </Typography>
      ),
      sortable: false,
      width: "10%", // Ancho fijo
    },
  ];

  // En una aplicación real, estos cálculos se harían en el backend
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  return (
    <Box>
      <Table<Migration>
        columns={columns}
        data={paginatedData}
        keyExtractor={(item) => item.id}
        isLoading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyMessage={emptyMessage}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Box>
  );
}
